/**
 * Hook: Set Organization Structure page logic.
 * Single Responsibility: facilities list state, can-continue validation, query cache cleanup.
 * Dependency Inversion: services imported at module level; logic testable via pure helpers.
 */

import { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FACILITIES_QUERY_KEY } from '../../../hooks/useFacilitiesInfinite';
import { FACILITIES_PAGE_BASED_QUERY_KEY } from '../../../hooks/useFacilitiesPageBased';
import { LOCATIONS_QUERY_KEY } from '../../../hooks/useLocationsInfinite';
import { QUERY_SCOPE } from '../constants';

/**
 * Pure: compute a stable key from facility list for dependency tracking.
 * @param {Array<{ id?: string }>} facilities
 * @returns {string}
 */
export function computeFacilityIdsKey(facilities) {
  return (facilities || [])
    .map((f) => f?.id)
    .filter(Boolean)
    .sort()
    .join(',');
}

/**
 * Locations API is only called when one facility is selected (LocationsPanel / useLocationsInfinite).
 * We do NOT call getLocationsByFacility per facility to avoid N requests.
 */
export function useSetOrganizationStructure() {
  const queryClient = useQueryClient();
  const [facilitiesFromList, setFacilitiesFromList] = useState([]);
  const canContinue = facilitiesFromList.length > 0;

  const handleFacilitiesChange = useCallback((list) => {
    const next = list ?? [];
    setFacilitiesFromList((prev) => {
      if (
        prev.length === next.length &&
        prev.every((f, i) => f?.id === next[i]?.id)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: [...FACILITIES_QUERY_KEY, QUERY_SCOPE] });
      queryClient.removeQueries({
        queryKey: [...FACILITIES_PAGE_BASED_QUERY_KEY, QUERY_SCOPE],
      });
      queryClient.removeQueries({ queryKey: [...LOCATIONS_QUERY_KEY, QUERY_SCOPE] });
    };
  }, [queryClient]);

  const refetchFacilities = useCallback(() => {
    return Promise.all([
      queryClient.refetchQueries({ queryKey: FACILITIES_QUERY_KEY }),
      queryClient.refetchQueries({ queryKey: FACILITIES_PAGE_BASED_QUERY_KEY }),
    ]);
  }, [queryClient]);

  return {
    facilitiesFromList,
    canContinue,
    handleFacilitiesChange,
    validateCanContinue: () => {},
    refetchFacilities,
    queryScope: QUERY_SCOPE,
  };
}
